import { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { FaRobot } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { Button, Card, EmptyState, KeyValueList, PageHeader, StatCard, StatGrid, StatusPill } from '../../../ui'
import { MailboxRef, mailboxLabel } from '../commsShared/mailboxes'
import { AgentActionsContext, readAgentActionsContext } from '../commsShared/agentActions'
import { ExportGroupBy, downloadCommsExport } from '../commsShared/exportDownload'
import { CategoryStats, ReportMenu, SenderLine, SenderSplit, StatLine, isMixed, sumSenders } from '../commsMailboxWorkspaceView/CategoryReport'
import { DeepDiveCard } from './DeepDiveCard'
import { CategoryMeta } from '../commsInboxView/ReplyDetailPane'
import { bucketLabel, isBucket, letterKindOf } from '../commsMailboxWorkspaceView/types'
import { sendKindLabel } from '../commsInboxView/shared'
import styles from './styles.module.scss'

/** One category (Emails) or outcome (Threads) at one level, from comms/inbox/bucket-categories. */
interface CategoryAtLevel {
    key: string | null
    label: string
    description: string | null
    nextAction: string | null
    total: number
    linked: number
    last7d: number
    lastReceivedAt: string | null
    emails: number | null
    automated: number | null
    /** Who they came from: a client, a lender, or neither. */
    from?: SenderSplit
    deepDivePending?: number | null
}

/** Category keys the list uses for "no category" and "automated mail only"; neither is a real outcome to deep dive. */
const PSEUDO_CATEGORY = /^__/

const fmt = (n: number) => n.toLocaleString('en-GB')

const levelLabel = (bucket: string) => (bucket === 'all' ? 'Everything received' : isBucket(bucket) ? bucketLabel(bucket) : bucket)

/** Back to the mailbox list filtered to exactly this card. */
const listHref = ({ mailbox, view, bucket, category }: AgentActionsContext) => {
    const params = new URLSearchParams({ tab: 'received', view, bucket })
    if (category) params.set('category', category)
    return `/comms/mailboxes/${encodeURIComponent(mailbox ?? 'all')}?${params.toString()}`
}

/**
 * Agent Actions: what happens next for the emails or threads in one category
 * at one level (a mailbox, Threads or Emails, a tab such as Letters of
 * complaint). Today it states the category, its figures and the agreed next
 * step, and lets the report be downloaded from here. The actions themselves,
 * run in groups, are built up here category by category, letters first.
 */
export const CommsAgentActionsView = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const ctx = useMemo(() => readAgentActionsContext(location.search), [location.search])
    const { mailbox, view, bucket, category } = ctx
    const unit = view === 'threads' ? 'thread' : 'email'

    const [mailboxes, setMailboxes] = useState<MailboxRef[]>([])
    const [level, setLevel] = useState<{ total: number; linked: number; items: CategoryAtLevel[] } | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [exporting, setExporting] = useState(false)

    useEffect(() => {
        let cancelled = false
        setLevel(null)
        setError(null)
        Promise.all([api({ url: 'comms/inbox/bucket-categories', data: { sourceMailbox: mailbox, bucket, view } }), api({ url: 'comms/inbox/stats', data: {} }).catch(() => null)])
            .then(([cats, stats]) => {
                if (cancelled) return
                if (cats.data?.success) setLevel({ total: cats.data.total || 0, linked: cats.data.linked || 0, items: cats.data.categories || [] })
                else setError(cats.data?.message || 'Could not load this category')
                if (stats?.data?.success) setMailboxes(stats.data.mailboxes || [])
            })
            .catch((e) => {
                if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load this category')
            })
        return () => {
            cancelled = true
        }
    }, [mailbox, bucket, view])

    const item: CategoryAtLevel | null = useMemo(() => {
        if (!level) return null
        if (category) return level.items.find((c) => (c.key ?? '__none') === category) ?? null
        return { key: null, label: view === 'threads' ? 'All threads' : 'All categories', description: null, nextAction: null, total: level.total, linked: level.linked, last7d: level.items.reduce((n, c) => n + c.last7d, 0), lastReceivedAt: null, emails: null, automated: null, from: sumSenders(level.items.map((c) => c.from)) }
    }, [level, category, view])

    /** Category labels for the thread modal, from the categories at this level. */
    const categoryMeta = useMemo(() => {
        const m = new Map<string, CategoryMeta>()
        for (const c of level?.items ?? []) if (c.key && !PSEUDO_CATEGORY.test(c.key)) m.set(c.key, { key: c.key, label: c.label, description: c.description, nextActionLabel: c.nextAction })
        return m
    }, [level])

    /** The deep dive reads the email that set a thread's outcome, so it exists for one real outcome in the Threads view. */
    const deepDiveScope = useMemo(() => (view === 'threads' && category && !PSEUDO_CATEGORY.test(category) ? { parent: category, mailbox, bucket } : null), [view, category, mailbox, bucket])

    const lines: StatLine[] = item
        ? [
              { value: item.total, label: `${unit}${item.total === 1 ? '' : 's'} at this level` },
              { value: item.linked, label: 'with a client or claim' },
              ...(view === 'threads' && item.emails !== null && item.emails !== item.total ? [{ value: item.emails, label: 'emails inside' }] : []),
              ...(item.automated ? [{ value: item.automated, label: 'automated emails inside' }] : []),
              { value: item.last7d, label: 'this week' },
          ]
        : []

    const letterKind = letterKindOf(bucket)

    const exportReport = async (groupBy: ExportGroupBy | null) => {
        if (bucket === 'waiting') return
        setExporting(true)
        try {
            const cat = category === '__none' ? 'unclassified' : category
            const slice =
                view === 'threads'
                    ? { kind: 'thread_outcome' as const, category, sendKind: letterKind, includeArchived: bucket === 'all', archivedOnly: bucket === 'archived' }
                    : letterKind
                    ? { kind: 'letter_type_category' as const, category: cat, sendKind: letterKind }
                    : { kind: 'category' as const, category: cat, includeArchived: bucket === 'all', archivedOnly: bucket === 'archived' }
            await downloadCommsExport({ ...slice, groupBy, mailbox, includeUnlinked: true, rowPerEmail: true }, `comms-actions-${(category ?? 'all').replace(/^__/, '')}${groupBy ? `-by-${groupBy}` : ''}.xlsx`)
        } finally {
            setExporting(false)
        }
    }

    // The page is reached from a category card ("See actions"); with no category in the URL there is nothing to act on.
    if (!category) return <Navigate to={mailbox ? `/comms/mailboxes/${encodeURIComponent(mailbox)}?tab=received&view=${view}&bucket=${encodeURIComponent(bucket)}` : '/comms/mailboxes'} replace />

    const title = item?.label ?? (category ? category.replace(/_/g, ' ') : 'All categories')

    return (
        <div className={styles.stack}>
            <PageHeader
                eyebrow="Comms Management · Agent Actions"
                title={title}
                backTo={listHref(ctx)}
                backLabel={`Back to ${mailbox ? mailboxLabel(mailbox, mailboxes) : 'all mailboxes'}`}
                intro={item?.description ?? 'What happens next for these, and the actions that can be run on them as a group.'}
                meta={
                    <>
                        <StatusPill size="sm" tone="info">
                            {view === 'threads' ? 'Threads · by outcome' : 'Emails · by category'}
                        </StatusPill>
                        <span>{mailbox ? mailboxLabel(mailbox, mailboxes) : 'All mailboxes'}</span>
                        <span>·</span>
                        <span>{levelLabel(bucket)}</span>
                    </>
                }
                actions={
                    <>
                        {bucket !== 'waiting' && item && item.total > 0 && <ReportMenu unit={unit} variant="ghost" exporting={exporting} onExport={exportReport} />}
                        <Button variant="neutral" onClick={() => navigate(listHref(ctx))}>
                            Open the {unit}s
                        </Button>
                    </>
                }
            />

            {error && <EmptyState tone="error" title="Could not load this category" description={error} />}
            {!error && !level && <EmptyState tone="loading" title="Loading…" />}
            {level && !item && <EmptyState title="Nothing here at the moment" description={`No ${unit}s in this category at this level right now.`} action={<Button onClick={() => navigate(listHref(ctx))}>Open the list</Button>} />}

            {item && (
                <>
                    <StatGrid minColumn={230}>
                        <StatCard wrapLabel label={view === 'threads' ? 'Threads standing here' : 'Emails in this category'} value={item.total} sub={<CategoryStats lines={lines.slice(1)} />} />
                        <StatCard wrapLabel label="With a client or claim" value={item.linked} unit={item.total ? ` · ${Math.round((item.linked / item.total) * 100)}%` : undefined} sub={<span>{item.total - item.linked === 0 ? 'Every one is on a known client or claim.' : `${fmt(item.total - item.linked)} still to be matched.`}</span>} />
                        <StatCard wrapLabel label="Arrived this week" value={item.last7d} sub={<span>{item.lastReceivedAt ? `Newest ${new Date(item.lastReceivedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Newest date not known'}</span>} />
                        {isMixed(item.from) && (
                            <StatCard
                                wrapLabel
                                label="Who wrote to us"
                                value={item.from.client >= item.from.lender && item.from.client >= item.from.other ? item.from.client : item.from.lender >= item.from.other ? item.from.lender : item.from.other}
                                unit={` ${item.from.client >= item.from.lender && item.from.client >= item.from.other ? 'clients' : item.from.lender >= item.from.other ? 'lenders' : 'neither'}`}
                                sub={<SenderLine from={item.from} />}
                            />
                        )}
                    </StatGrid>

                    {deepDiveScope && <DeepDiveCard scope={deepDiveScope} scopeLabel={`${mailbox ? mailboxLabel(mailbox, mailboxes) : 'All mailboxes'} · ${levelLabel(bucket)}`} letterKind={letterKind} bucket={bucket} categoryMeta={categoryMeta} mailboxes={mailboxes} />}

                    <div className={styles.twoUp}>
                        <Card title="Where this sits" subtitle="The level these figures are counted at.">
                            <KeyValueList
                                stacked
                                items={[
                                    { label: 'Mailbox', value: mailbox ? mailboxLabel(mailbox, mailboxes) : 'All mailboxes' },
                                    { label: 'Counted as', value: view === 'threads' ? 'Threads, by where each one stands (its newest non-automated email)' : 'Emails, each by its own category' },
                                    { label: 'Level', value: letterKind ? `Replies to a ${sendKindLabel(letterKind).toLowerCase()} we sent` : levelLabel(bucket) },
                                    { label: view === 'threads' ? 'Outcome' : 'Category', value: item.label },
                                    { label: 'Agreed next step', value: item.nextAction ?? '—' },
                                ]}
                            />
                        </Card>

                        <Card title="Agent actions" subtitle={`Run on every ${unit} in this group at once.`}>
                            <EmptyState
                                bare
                                icon={<FaRobot />}
                                title="No actions set up for this category yet"
                                description={
                                    <>
                                        This is where the next step for <strong>{item.label}</strong> becomes something that can be run in bulk: chasing the lender, asking the client for information, updating the case, or filing the thread. Actions are being built up category by category, starting with replies to our letters of complaint, then the other letters we send, then the rest of the mailbox.
                                        {item.nextAction && (
                                            <>
                                                {' '}
                                                Today the agreed next step is: <strong>{item.nextAction}</strong>.
                                            </>
                                        )}
                                    </>
                                }
                                action={
                                    <Button variant="neutral" onClick={() => navigate(listHref(ctx))}>
                                        Work through the {unit}s by hand
                                    </Button>
                                }
                            />
                        </Card>
                    </div>
                </>
            )}
        </div>
    )
}
